import { useTranslation } from '@pancakeswap/localization'
import { SmartRouter, SmartRouterTrade } from '@pancakeswap/smart-router/evm'
import { Currency, CurrencyAmount, Percent, TradeType } from '@pancakeswap/sdk'
import { Dots, Flex, Text } from '@pancakeswap/uikit'
import { formatAmount } from '@pancakeswap/utils/formatFractions'
import { memo, useMemo, useState } from 'react'
import { Field } from '@/state/swap/actions'
import { warningSeverity } from '@/utils/exchange'
import { BUYBACK_FEE, LP_HOLDERS_FEE, TOTAL_FEE, TREASURY_FEE } from '@/config/constants/info'
import { formatExecutionPrice as mmFormatExecutionPrice } from '@/views/Swap/MMLinkPools/utils/exchange'
import FormattedPriceImpact from '../../components/FormattedPriceImpact'
import { SwapCallbackError } from '../../components/styleds'
import { formatExecutionPrice } from '../utils/exchange'
import { Typography } from '@/components/ui/typography'
import { Button } from '@/components/ui/button'
import { SlippageTooltip } from '@/components/tooltips/slippage'
import { PriceImpactTooltip } from '@/components/tooltips/price-impact'
import { TradingFeeTooltip } from '@/components/tooltips/trading-fee'
import { ArrowRightLeftIcon } from 'lucide-react'

export const SwapModalFooter = memo(function SwapModalFooter({
  priceImpact: priceImpactWithoutFee,
  lpFee: realizedLPFee,
  inputAmount,
  outputAmount,
  trade,
  tradeType,
  slippageAdjustedAmounts,
  isEnoughInputBalance,
  onConfirm,
  swapErrorMessage,
  disabledConfirm,
  isMM,
  isRFQReady,
  currencyBalances,
}: {
  trade?: SmartRouterTrade<TradeType>
  tradeType: TradeType
  lpFee?: CurrencyAmount<Currency>
  inputAmount: CurrencyAmount<Currency>
  outputAmount: CurrencyAmount<Currency>
  priceImpact?: Percent
  slippageAdjustedAmounts: { [field in Field]?: CurrencyAmount<Currency> }
  isEnoughInputBalance?: boolean
  swapErrorMessage?: string | undefined
  disabledConfirm: boolean
  isMM?: boolean
  isRFQReady?: boolean
  currencyBalances: {
    INPUT?: CurrencyAmount<Currency>
    OUTPUT?: CurrencyAmount<Currency>
  }
  onConfirm: () => void
}) {
  const { t } = useTranslation()
  const [showInverted, setShowInverted] = useState<boolean>(false)
  const severity = warningSeverity(priceImpactWithoutFee)

  const totalFeePercent = `${(TOTAL_FEE * 100).toFixed(2)}%`
  const lpHoldersFeePercent = `${(LP_HOLDERS_FEE * 100).toFixed(2)}%`
  const treasuryFeePercent = `${(TREASURY_FEE * 100).toFixed(4)}%`
  const buyBackFeePercent = `${(BUYBACK_FEE * 100).toFixed(4)}%`

  const executionPriceDisplay = useMemo(() => {
    if (isMM) {
      return mmFormatExecutionPrice(trade, showInverted)
    }

    const price = SmartRouter.getExecutionPrice(trade) ?? undefined
    return formatExecutionPrice(price, inputAmount, outputAmount, showInverted)
  }, [inputAmount, isMM, outputAmount, trade, showInverted])

  return (
    <>
      <div className={'mt-6 flex flex-col gap-2 text-sm'}>
        <div className={'flex items-center justify-between'}>
          <Typography variant={'muted'}>{t('Price')}</Typography>
          <Typography
            className={'flex items-center gap-1 cursor-pointer'}
            onClick={() => setShowInverted(!showInverted)}
          >
            {executionPriceDisplay}
            <ArrowRightLeftIcon width={12} height={12} />
          </Typography>
        </div>

        <div className={'flex items-center justify-between'}>
          <div className={'flex items-center gap-1'}>
            <Typography variant={'muted'}>
              {tradeType === TradeType.EXACT_INPUT ? t('Minimum received') : t('Maximum sold')}
            </Typography>
            <SlippageTooltip />
          </div>
          <Typography variant={'small'}>
            {tradeType === TradeType.EXACT_INPUT
              ? formatAmount(slippageAdjustedAmounts[Field.OUTPUT], 4) ?? '-'
              : formatAmount(slippageAdjustedAmounts[Field.INPUT], 4) ?? '-'}
            &nbsp;
            {tradeType === TradeType.EXACT_INPUT ? outputAmount.currency.symbol : inputAmount.currency.symbol}
          </Typography>
        </div>
        <div className={'flex items-center justify-between'}>
          <div className={'flex items-center gap-1'}>
            <Typography variant={'muted'}>{t('Price Impact')}</Typography>
            <PriceImpactTooltip />
          </div>
          {isMM ? <Text color="textSubtle">--</Text> : <FormattedPriceImpact priceImpact={priceImpactWithoutFee} />}
        </div>
        <div className={'flex items-center justify-between'}>
          <div className={'flex items-center gap-1'}>
            <Typography variant={'muted'}>{t('Trading Fee')}</Typography>
            <TradingFeeTooltip isMM={isMM ?? false} />
          </div>
          {realizedLPFee ? (
            <Flex>
              <Typography variant={'small'}>{`${formatAmount(realizedLPFee, 6)} ${
                inputAmount.currency.symbol
              }`}</Typography>
            </Flex>
          ) : (
            <Text fontSize="14px" textAlign="right">
              -
            </Text>
          )}
        </div>
      </div>

      <Button
        size={'lg'}
        className={'w-full mt-6'}
        variant={severity > 2 ? 'destructive' : 'zap'}
        onClick={onConfirm}
        disabled={isMM ? disabledConfirm || !isRFQReady : disabledConfirm}
      >
        {isMM && !isRFQReady ? (
          <Dots>{t('Checking RFQ with MM')}</Dots>
        ) : severity > 2 || (tradeType === TradeType.EXACT_OUTPUT && !isEnoughInputBalance) ? (
          t('Swap Anyway')
        ) : (
          t('Confirm Swap')
        )}
      </Button>

      {swapErrorMessage ? <SwapCallbackError error={swapErrorMessage} /> : null}
    </>
  )
})
